import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IAppliedFilters,
  IBenefitFilters,
  IBenefitItem,
  IBenefitStats,
  IFilterValues,
  IPagination,
} from "../interfaces/benefit";
import { getBenefitList, getBenefitStats } from "../services/benefitService";

/** Intervalo de auto-refresco de la tabla (5 min) */
const AUTO_REFRESH_MS: number = 5 * 60 * 1000;

/** Ventana desde la última interacción para considerar activo al usuario (5 min) */
const ACTIVITY_WINDOW_MS: number = 5 * 60 * 1000;

/** Borrador de los inputs de filtro (texto en curso + selección de respuesta) */
export const EMPTY_FILTERS: IFilterValues = {
  DNI: "",
  name: "",
  loadDate: "",
  sendDate: "",
  answerDate: "",
  answer: "",
  file: "",
};

/** Filtros aplicados: cada campo acumula varios valores (varios RUT, etc.) */
export const EMPTY_APPLIED: IAppliedFilters = {
  DNI: [],
  name: [],
  loadDate: [],
  sendDate: [],
  answerDate: [],
  answer: [],
  file: [],
};

export const useBenefits = () => {
  const [benefits, setBenefits] = useState<IBenefitItem[]>([]);
  const [pagination, setPagination] = useState<IPagination | null>(null);
  const [stats, setStats] = useState<IBenefitStats | null>(null);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(true);

  // borrador de los inputs vs. filtros efectivamente aplicados (multivalor)
  const [draftValues, setDraftValues] = useState<IFilterValues>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<IAppliedFilters>(EMPTY_APPLIED);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // descarta respuestas de peticiones que quedaron obsoletas
  const requestIdRef = useRef<number>(0);

  // solo viajan al endpoint los campos con al menos un valor aplicado
  const apiFilters: IBenefitFilters = useMemo(() => {
    const filters: IBenefitFilters = {};

    (Object.keys(appliedFilters) as Array<keyof IFilterValues>).forEach((key) => {
      if (appliedFilters[key].length > 0) filters[key] = appliedFilters[key];
    });

    return filters;
  }, [appliedFilters]);

  const fetchBenefits = useCallback(
    async (silent: boolean = false): Promise<void> => {
      const requestId: number = ++requestIdRef.current;

      // el refresco silencioso no muestra skeleton ni pisa la data si falla
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      try {
        const response = await getBenefitList({ page, limit, filters: apiFilters });

        if (requestId !== requestIdRef.current) return;

        setBenefits(response.benefits ?? []);
        setPagination(response.pagination ?? null);
        setError(null);
      } catch (requestError) {
        if (requestId !== requestIdRef.current) return;

        console.error("ERROR AL OBTENER BENEFICIOS", requestError);

        if (!silent) {
          setBenefits([]);
          setPagination(null);
          setError("No pudimos cargar los registros. Intenta nuevamente.");
        }
      } finally {
        if (!silent && requestId === requestIdRef.current) setIsLoading(false);
      }
    },
    [page, limit, apiFilters]
  );

  useEffect(() => {
    void fetchBenefits();
  }, [fetchBenefits]);

  // los totales son globales: no dependen de la pagina ni de los filtros
  const fetchStats = useCallback(async (silent: boolean = false): Promise<void> => {
    if (!silent) setIsStatsLoading(true);

    try {
      const data: IBenefitStats = await getBenefitStats();
      setStats(data);
    } catch (statsError) {
      console.error("ERROR AL OBTENER TOTALES", statsError);
      if (!silent) setStats(null);
    } finally {
      if (!silent) setIsStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  // refresco manual (con skeleton) vs. refresco silencioso (auto cada 5 min)
  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([fetchBenefits(false), fetchStats(false)]);
  }, [fetchBenefits, fetchStats]);

  const refreshSilently = useCallback(async (): Promise<void> => {
    await Promise.all([fetchBenefits(true), fetchStats(true)]);
  }, [fetchBenefits, fetchStats]);

  // el intervalo lee siempre el refresco vigente sin reiniciarse en cada cambio
  const refreshSilentlyRef = useRef(refreshSilently);
  useEffect(() => {
    refreshSilentlyRef.current = refreshSilently;
  }, [refreshSilently]);

  // auto-refresco silencioso cada 5 min, solo si el usuario interactuó hace poco
  // y la pestaña está visible: evita golpear el backend con pestañas olvidadas.
  useEffect(() => {
    const lastActivity = { at: Date.now() };
    const markActive = (): void => {
      lastActivity.at = Date.now();
    };

    const activityEvents: string[] = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];
    activityEvents.forEach((event) =>
      window.addEventListener(event, markActive, { passive: true })
    );

    const interval = window.setInterval(() => {
      const isVisible: boolean = document.visibilityState === "visible";
      const isActive: boolean = Date.now() - lastActivity.at <= ACTIVITY_WINDOW_MS;
      if (isVisible && isActive) void refreshSilentlyRef.current();
    }, AUTO_REFRESH_MS);

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, markActive)
      );
      window.clearInterval(interval);
    };
  }, []);

  // texto que la persona va escribiendo en un input (aún sin aplicar)
  const setDraft = useCallback((key: keyof IFilterValues, value: string): void => {
    setDraftValues((current) => ({ ...current, [key]: value }));
  }, []);

  // Enter en un campo de texto: agrega el valor a la lista y vacía el input
  const applyTextFilter = useCallback(
    (key: keyof IFilterValues, rawValue: string): void => {
      const value: string = rawValue.trim();
      if (value === "") return;

      setAppliedFilters((current) => {
        if (current[key].includes(value)) return current;
        return { ...current, [key]: [...current[key], value] };
      });
      setDraftValues((current) => ({ ...current, [key]: "" }));
      setPage(1);
    },
    []
  );

  // el select de respuesta aplica al instante y reemplaza el valor anterior
  const applyAnswerFilter = useCallback((value: string): void => {
    setAppliedFilters((current) => ({
      ...current,
      answer: value === "" ? [] : [value],
    }));
    setDraftValues((current) => ({ ...current, answer: value }));
    setPage(1);
  }, []);

  const removeFilter = useCallback(
    (key: keyof IFilterValues, value: string): void => {
      setAppliedFilters((current) => ({
        ...current,
        [key]: current[key].filter((item) => item !== value),
      }));
      // al quitar la respuesta, el select vuelve a "Todas"
      if (key === "answer") {
        setDraftValues((current) => ({ ...current, answer: "" }));
      }
      setPage(1);
    },
    []
  );

  const clearFilters = useCallback((): void => {
    setAppliedFilters(EMPTY_APPLIED);
    setDraftValues(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const hasActiveFilters: boolean = useMemo(
    () => Object.values(appliedFilters).some((values) => values.length > 0),
    [appliedFilters]
  );

  return {
    benefits,
    pagination,
    stats,
    isStatsLoading,
    draftValues,
    appliedFilters,
    apiFilters,
    hasActiveFilters,
    page,
    limit,
    isLoading,
    error,
    setDraft,
    applyTextFilter,
    applyAnswerFilter,
    removeFilter,
    clearFilters,
    setPage,
    setLimit,
    refresh,
  };
};
